import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Asset, GenerationStatusResponse } from "@/types";

/**
 * Triggers background processing for processing generations
 * Polls /generation/:id/status with proper request sequencing (no overlaps)
 */
export function useBackgroundProcessing(assets: Asset[]) {
  const queryClient = useQueryClient();
  const pollingRef = useRef<
    Map<string, { isPolling: boolean; timeoutId?: number }>
  >(new Map());

  useEffect(() => {
    const processingGenerations = assets.filter(
      (asset) =>
        asset.source === "generation" &&
        (asset.status === "processing" || asset.status === "pending"),
    );

    const processingIds = new Set(processingGenerations.map((a) => a.id));

    // Start polling for new processing generations
    processingGenerations.forEach((asset) => {
      if (pollingRef.current.has(asset.id)) return; // Already polling

      console.log(`[BackgroundProcessing] Start polling: ${asset.id}`);

      const pollGeneration = async () => {
        const state = pollingRef.current.get(asset.id);
        if (!state) return;

        // Skip if already polling (request in-flight)
        if (state.isPolling) {
          console.log(
            `[BackgroundProcessing] Skipping ${asset.id} (request in-flight)`,
          );
          return;
        }

        state.isPolling = true;

        try {
          const response = await api.get<GenerationStatusResponse>(
            `/generation/${asset.id}/status`,
          );

          const newStatus = response.generation.status;
          console.log(`[BackgroundProcessing] ${asset.id}: ${newStatus}`);

          // If completed or failed, stop polling and refresh
          if (newStatus === "completed" || newStatus === "failed") {
            console.log(`[BackgroundProcessing] ✓ ${asset.id} finished`);

            // Stop polling
            if (state.timeoutId) {
              clearTimeout(state.timeoutId);
            }
            pollingRef.current.delete(asset.id);

            // Refresh assets (only now!)
            queryClient.invalidateQueries({ queryKey: ["assets"] });
          } else {
            // Still processing - schedule next poll after 5s
            state.isPolling = false;
            state.timeoutId = window.setTimeout(pollGeneration, 5000);
          }
        } catch (err: any) {
          console.warn(
            `[BackgroundProcessing] Error ${asset.id}:`,
            err.message,
          );

          // On error, retry after 5s
          state.isPolling = false;
          state.timeoutId = window.setTimeout(pollGeneration, 5000);
        }
      };

      // Initialize polling state and start immediately
      pollingRef.current.set(asset.id, { isPolling: false });
      pollGeneration();
    });

    // Stop polling for generations no longer in processing state
    Array.from(pollingRef.current.keys()).forEach((id) => {
      if (!processingIds.has(id)) {
        const state = pollingRef.current.get(id);
        if (state?.timeoutId) {
          clearTimeout(state.timeoutId);
        }
        pollingRef.current.delete(id);
        console.log(`[BackgroundProcessing] Stop polling: ${id}`);
      }
    });

    // Cleanup on unmount
    return () => {
      pollingRef.current.forEach((state) => {
        if (state.timeoutId) {
          clearTimeout(state.timeoutId);
        }
      });
      pollingRef.current.clear();
    };
  }, [assets, queryClient]);
}
